import React from 'react'
import { motion } from 'framer-motion'
import { Card, CardContent } from '../ui/card'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import {
  Award,
  Users,
  Leaf,
  Shield,
  Clock,
  Star,
  CheckCircle,
  ArrowRight,
  Heart,
  Lightbulb,
} from 'lucide-react'
import ImagePlaceholder from '../../utils/ImagePlaceholder'
import { useNavigate } from 'react-router'
import { ImageWithFallback } from '../../utils/ImageWithFallback'

import service1 from '../../assets/about/1.jpg'
import service2 from '../../assets/about/2.jpg'
import service3 from '../../assets/about/3.jpg'
import service4 from '../../assets/about/4.jpg'

import valueImg1 from '../../assets/core-values/1.png'
import valueImg2 from '../../assets/core-values/2.png'
import valueImg3 from '../../assets/core-values/3.png'
import valueImg4 from '../../assets/core-values/4.png'

export function AboutComponent() {
  const navigate = useNavigate()
  const stats = [
    {
      number: '15+',
      label: 'Years Experience',
      icon: <Clock className="w-6 h-6" />,
    },
    {
      number: '500+',
      label: 'Happy Clients',
      icon: <Users className="w-6 h-6" />,
    },
    {
      number: '1000+',
      label: 'Projects Completed',
      icon: <Award className="w-6 h-6" />,
    },
    {
      number: '4.9/5',
      label: 'Customer Rating',
      icon: <Star className="w-6 h-6" />,
    },
  ]

  const values = [
    {
      icon: valueImg1,
      title: 'Eco-Friendly Practices',
      description:
        'We use sustainable methods and native plants to create environmentally responsible landscapes.',
    },
    {
      icon: valueImg2,
      title: 'Customer-Focused',
      description:
        'Your satisfaction is our priority. We listen to your needs and exceed your expectations.',
    },
    {
      icon: valueImg3,
      title: 'Innovative Solutions',
      description:
        'We stay current with the latest landscaping trends and technologies for the best results.',
    },
    {
      icon: valueImg4,
      title: 'Quality Guarantee',
      description:
        'We stand behind our work with comprehensive warranties and ongoing support.',
    },
  ]

  const certifications = [
    'Licensed Landscape Contractor',
    'Certified Arborist (ISA)',
    'Irrigation Association Member',
    'EPA Certified Applicator',
    'Better Business Bureau A+',
  ]

  return (
    <>
      <section id="about" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Main About Content */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <Badge className="mb-4 bg-green-100 text-green-800 border-green-200">
                <Leaf className="w-4 h-4 mr-2" />
                About Gardenly Care
              </Badge>

              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Creating Beautiful Landscapes for Over 15 Years
              </h2>

              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                At Gardenly Care, we believe that your outdoor space should be a
                reflection of your personality and lifestyle. Since 2008, we've
                been transforming ordinary yards into extraordinary outdoor
                environments that our clients love.
              </p>

              <p className="text-gray-600 mb-8 leading-relaxed">
                Our team of experienced professionals combines creativity with
                technical expertise to deliver landscaping solutions that are both
                beautiful and functional. From initial design concepts to ongoing
                maintenance, we're committed to exceeding your expectations every
                step of the way.
              </p>

              {/* Certifications */}
              <div className="mb-8">
                <h4 className="font-semibold text-gray-900 mb-4">
                  Certifications & Memberships:
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {certifications.map((cert, index) => (
                    <motion.div
                      key={index}
                      className="flex items-center"
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <CheckCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0" />
                      <span className="text-gray-600 text-sm">{cert}</span>
                    </motion.div>
                  ))}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  className="bg-green-600 hover:bg-green-700 text-white"
                  onClick={() => {
                    navigate('/contact')
                  }}
                >
                  Start Your Project
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => {
                    navigate('/portfolio')
                  }}
                >
                  View Our Work
                </Button>
              </div>
            </motion.div>

            {/* Image Grid */}
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <motion.div
                    className="relative overflow-hidden rounded-2xl"
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ImageWithFallback
                      src={service1}
                      alt="Garden design"
                      className="w-full h-64 object-cover"
                    />
                    {/* <ImagePlaceholder width={284} height={256} /> */}
                  </motion.div>
                  <motion.div
                    className="relative overflow-hidden rounded-2xl"
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ImageWithFallback
                      src={service2}
                      alt="Tree services"
                      className="w-full h-32 object-cover"
                    />
                    {/* <ImagePlaceholder width={284} height={128} /> */}
                  </motion.div>
                </div>
                <div className="space-y-4 pt-8">
                  <motion.div
                    className="relative overflow-hidden rounded-2xl"
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ImageWithFallback
                      src={service3}
                      alt="Hardscaping"
                      className="w-full h-32 object-cover"
                    />
                    {/* <ImagePlaceholder width={284} height={256} /> */}
                  </motion.div>
                  <motion.div
                    className="relative overflow-hidden rounded-2xl"
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.3 }}
                  >
                    {/* <ImagePlaceholder width={284} height={128} /> */}
                    <ImageWithFallback
                      src={service4}
                      alt="Lawn maintenance"
                      className="w-full h-64 object-cover"
                    />
                  </motion.div>
                </div>
              </div>

              {/* Floating Stats Card */}
              <motion.div
                className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-6 border border-gray-100"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
              >
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600 mb-1">
                    15+
                  </div>
                  <div className="text-sm text-gray-600">Years Experience</div>
                </div>
              </motion.div>
            </motion.div>
          </div>

          {/* Stats Section */}
          <motion.div
            className="grid grid-cols-2 lg:grid-cols-4 gap-8 mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
              >
                <Card className="p-6 hover:shadow-lg transition-shadow border-0 shadow-md">
                  <CardContent className="p-0">
                    <div className="flex justify-center mb-4 text-green-600">
                      {stat.icon}
                    </div>
                    <div className="text-3xl font-bold text-gray-900 mb-2">
                      {stat.number}
                    </div>
                    <div className="text-gray-600">{stat.label}</div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>

          {/* Values Section */}
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              Our Core Values
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              These principles guide everything we do and reflect our commitment
              to excellence in landscaping.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                className="text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                whileHover={{ y: -5 }}
              >
                <Card className="h-full p-6 hover:shadow-lg transition-all border-0 shadow-md hover:border-green-200">
                  <CardContent className="p-0">
                    <div className="mb-4 flex justify-center">
                      <ImageWithFallback
                        src={value.icon}
                        alt={value.title}
                        className="w-full object-cover"
                      />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">
                      {value.title}
                    </h4>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>

  )
}


